import { useEffect, useState } from "preact/hooks";
import { getAddress, zeroAddress } from "viem";
import { api } from "../api";
import { registryAbi } from "../contracts";
import { Notice, QyooImage, Spinner, TechnicalDetails } from "../components";
import type { ResolutionEnvelope } from "../types";
import type { WalletState } from "../wallet";

export function DetailView(props: {
  qyoo: number;
  wallet: WalletState;
  navigate(path: string): void;
}) {
  const [details, setDetails] = useState<ResolutionEnvelope | null>(null);
  const [resolver, setResolver] = useState("");
  const [controller, setController] = useState("");
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);

  const load = async () => {
    try {
      const result = await api.qyoo(props.qyoo);
      setDetails(result);
      setResolver(result.blockchain.resolver ?? "");
      setController(result.blockchain.controller ?? "");
    } catch (error) {
      setMessage({ kind: "error", text: error instanceof Error ? error.message : "Could not load Qyoo" });
    }
  };
  useEffect(() => { void load(); }, [props.qyoo]);

  const account = props.wallet.account?.toLowerCase();
  const isOwner = !!account && details?.blockchain.owner?.toLowerCase() === account;
  const isController = !!account && details?.blockchain.controller?.toLowerCase() === account;
  const unsupportedNetwork = details !== null && ![1,11155111,31337].includes(details.chain.id);

  async function update(functionName: "setResolver" | "setController" | "safeTransferFrom") {
    if (!details?.blockchain.owner || !props.wallet.account) return;
    setBusy(functionName);
    setMessage(null);
    try {
      if (unsupportedNetwork) throw new Error("This network is unsupported");
      if (props.wallet.chainId !== details.chain.id) await props.wallet.switchChain(details.chain.id);
      let args: readonly unknown[];
      if (functionName === "setResolver") {
        const url = new URL(resolver);
        if (!(url.protocol === "https:" || (details.chain.id === 31337 && url.protocol === "http:" && ["localhost", "127.0.0.1"].includes(url.hostname))) || url.username || url.password || url.hash) {
          throw new Error("Use a credential-free HTTPS manifest URL. Other formats are not supported by this public beta.");
        }
        args = [BigInt(props.qyoo), resolver];
      } else if (functionName === "setController") {
        args = [BigInt(props.qyoo), controller.trim() ? getAddress(controller) : zeroAddress];
      } else {
        args = [details.blockchain.owner, getAddress(recipient), BigInt(props.qyoo)];
      }
      const hash = await props.wallet.writeContract({
        chainId: details.chain.id,
        address: details.chain.registry,
        abi: registryAbi,
        functionName,
        args
      });
      setMessage({ kind: "success", text: `Transaction ${hash.slice(0, 10)}… submitted. Waiting for confirmation…` });
      await props.wallet.waitForTransaction(hash, details.chain.id);
      setMessage({ kind: "success", text: "Ethereum confirmed the change." });
      await load();
    } catch (error) {
      setMessage({ kind: "error", text: error instanceof Error ? error.message : "Wallet operation failed" });
    } finally {
      setBusy(null);
    }
  }

  if (!details && !message) return <div class="page-shell"><Spinner label="Reading Qyoo from Ethereum…" /></div>;
  if (!details) return <div class="page-shell">{message && <Notice kind={message.kind}>{message.text}</Notice>}</div>;
  const theme = details.experience?.presentation;

  return (
    <div class="page-shell detail-page">
      <button class="text-button back" onClick={() => props.navigate("/my-qyoos")}>← My Qyoos</button>
      <section class="identity-hero" style={{ background: theme?.background ?? "#171717", color: theme?.foreground ?? "#FFFFFF" }}>
        <QyooImage qyoo={props.qyoo} />
        <div><p class="eyebrow">{isOwner ? "You own this Qyoo" : isController ? "You control its experience" : "Qyoo identity"}</p><h1>{theme?.title ?? `Qyoo #${props.qyoo}`}</h1><p>{theme?.subtitle ?? "No published experience metadata yet."}</p><span class="id-chip">Raw ID · {props.qyoo}</span></div>
      </section>
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {unsupportedNetwork && <Notice kind="error">Control transactions are unavailable on this network.</Notice>}
      <div class="detail-grid">
        <section class="panel">
          <div class="section-title"><div><p class="eyebrow">Experience</p><h2>Where it comes alive</h2></div>{(isOwner || isController) && <button class="button primary compact" onClick={() => props.navigate(`/qyoo/${props.qyoo}/edit`)}>Edit experience</button>}</div>
          <p>Status: <strong>{details.resolution.status}</strong>{details.resolution.source ? ` via ${details.resolution.source} resolver` : ""}</p>
          <label for="resolver">Resolver</label>
          <input id="resolver" value={resolver} disabled={!isOwner && !isController} onInput={(event) => setResolver(event.currentTarget.value)} />
          {(isOwner || isController) && <button class="button ghost" disabled={busy !== null || unsupportedNetwork} onClick={() => void update("setResolver")}>Save resolver on Ethereum</button>}
        </section>
        <section class="panel download-panel">
          <p class="eyebrow">Place it in the world</p><h2>Download your Qyoo</h2>
          <p>Both formats include a conservative white quiet zone outside the unchanged carrier geometry.</p>
          <div class="button-row"><a class="button ghost" download={`qyoo-${props.qyoo}.svg`} href={`/v1/qyoo/${props.qyoo}/image.svg`}>Download SVG</a><a class="button ghost" download={`qyoo-${props.qyoo}.png`} href={`/v1/qyoo/${props.qyoo}/image.png?size=2048`}>Download PNG</a></div>
        </section>
      </div>
      {isOwner && (
        <section class="panel ownership-controls">
          <p class="eyebrow">Owner controls</p><h2>Delegate or transfer</h2>
          <div class="control-grid">
            <div><label for="controller">Experience controller</label><input id="controller" placeholder="0x… (blank revokes)" value={controller} onInput={(event) => setController(event.currentTarget.value)} /><button class="button ghost" disabled={busy !== null || unsupportedNetwork} onClick={() => void update("setController")}>{controller.trim() ? "Appoint controller" : "Revoke controller"}</button><p class="field-help">A controller can change the resolver or publish hosted experiences, never transfer ownership.</p></div>
            <div><label for="recipient">Transfer owner</label><input id="recipient" placeholder="New owner 0x…" value={recipient} onInput={(event) => setRecipient(event.currentTarget.value)} /><button class="button danger" disabled={busy !== null || !recipient || unsupportedNetwork} onClick={() => void update("safeTransferFrom")}>Transfer Qyoo</button><p class="field-help">Uses ordinary ERC-721 transfer. The current delegated controller is cleared automatically.</p></div>
          </div>
        </section>
      )}
      <TechnicalDetails rows={[
        ["Ethereum network", `${details.chain.network} · chain ${details.chain.id}`],
        ["Registry", details.chain.registry],
        ["Token ID", String(props.qyoo)],
        ["Owner", details.blockchain.owner],
        ["Controller", details.blockchain.controller],
        ["Resolver", details.blockchain.resolver]
      ]} />
    </div>
  );
}
